import { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, Trash2, User, Sparkles } from "lucide-react";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/PageHeader";
import { MarkdownLite } from "@/components/MarkdownLite";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SESSION = "default";

export default function Agent() {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const SUGGESTIONS = [t("ag.s1"), t("ag.s2"), t("ag.s3"), t("ag.s4")];

  useEffect(() => {
    api.get(`/ai/history?session_id=${SESSION}`).then(({ data }) => setMessages(data || [])).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    let streamed = false;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API}/ai/chat-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ session_id: SESSION, message: msg, lang }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      setMessages((m) => [...m, { role: "assistant", content: "" }]);
      streamed = true;
      setLoading(false);
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      if (!streamed) {
        try {
          const { data } = await api.post("/ai/chat", { session_id: SESSION, message: msg, lang });
          setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch {
          toast.error(t("ag.failed"));
          setMessages((m) => [...m, { role: "assistant", content: t("ag.errMsg") }]);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    await api.post("/ai/clear", { session_id: SESSION });
    setMessages([]);
    toast.success(t("ag.cleared"));
  };

  return (
    <div className="flex h-[calc(100vh-6rem)] flex-col lg:h-[calc(100vh-3rem)]">
      <PageHeader
        title={t("ag.title")}
        subtitle={t("ag.sub")}
        icon={Bot}
        actions={
          messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clear} className="gap-1.5 text-slate-400 hover:text-white" data-testid="clear-chat-btn">
              <Trash2 className="h-4 w-4" /> {t("ag.clear")}
            </Button>
          )
        }
      />

      <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-slate-800 bg-[#0E1118]">
        <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6" data-testid="chat-messages">
          {messages.length === 0 && !loading && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600/15">
                <Sparkles className="h-7 w-7 text-blue-400" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white">{t("ag.help")}</h3>
              <p className="mt-1 max-w-sm text-sm text-slate-500">{t("ag.helpSub")}</p>
              <div className="mt-5 grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="suggestion-btn"
                    className="rounded-lg border border-slate-800 bg-slate-900/50 p-3 text-left text-xs text-slate-300 transition-colors hover:border-blue-800 hover:bg-blue-950/30">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-600/20"><Bot className="h-4 w-4 text-blue-400" /></div>
              )}
              <div className={`max-w-[85%] rounded-lg p-3 text-sm ${m.role === "user" ? "bg-slate-800 text-white" : "border border-blue-900/40 bg-blue-950/20 text-slate-200"}`}>
                {m.role === "assistant" ? <MarkdownLite text={m.content} /> : <span className="whitespace-pre-wrap">{m.content}</span>}
              </div>
              {m.role === "user" && (
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-800"><User className="h-4 w-4 text-slate-400" /></div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex gap-3">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-600/20"><Bot className="h-4 w-4 text-blue-400" /></div>
              <div className="flex items-center gap-2 rounded-lg border border-blue-900/40 bg-blue-950/20 p-3 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("ag.thinking")}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-slate-800 p-3">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("ag.inputPh")}
              rows={1}
              data-testid="chat-input"
              className="min-h-[44px] resize-none border-slate-700 bg-slate-900 text-sm focus-visible:ring-blue-500"
            />
            <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="chat-send-btn"
              className="h-11 gap-1.5 bg-blue-600 hover:bg-blue-500">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
